import { useState, useEffect, useMemo } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { KeyRound, Users, Eye, EyeOff, Copy, RefreshCw, AlertTriangle, Pencil, LogIn } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '@/utils/errorHandler';
import { useAuth } from '@/context/AuthContext';
import { MasterKeyCard } from '@/components/admin/MasterKeyCard';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_COLOURS = {
  admin: 'bg-red-100 text-red-800 border-red-300',
  super_admin: 'bg-red-100 text-red-800 border-red-300',
  auto_spares_super: 'bg-purple-100 text-purple-800 border-purple-300',
  auto_spares_admin: 'bg-purple-100 text-purple-800 border-purple-300',
  auto_spares_salesman: 'bg-blue-100 text-blue-800 border-blue-300',
  clerk: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  internal_staff: 'bg-amber-100 text-amber-800 border-amber-300',
  driver: 'bg-orange-100 text-orange-800 border-orange-300',
};

export default function UserManager() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(null);            // user_id being reset
  const [resetDialog, setResetDialog] = useState(null);        // { user, newPw }
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPw, setBulkPw] = useState('');
  const [bulkIncludeAdmins, setBulkIncludeAdmins] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [filter, setFilter] = useState('');
  const [editUser, setEditUser] = useState(null); // { id, full_name, username, email }
  const [editSaving, setEditSaving] = useState(false);

  const isMasterAdmin = useMemo(() => (
    currentUser?.username === 'michael'
    || currentUser?.email === 'admin@jsmtrading.com'
    || (currentUser?.permissions || {}).is_master_admin
  ), [currentUser]);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/all-users`);
      setUsers(r.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!filter) return users;
    const q = filter.toLowerCase();
    return users.filter(u =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.username || '').toLowerCase().includes(q) ||
      (u.full_name || '').toLowerCase().includes(q) ||
      (u.role || '').toLowerCase().includes(q),
    );
  }, [users, filter]);

  const handleResetSingle = async (user) => {
    const proposed = window.prompt(
      `New password for ${user.email || user.username}?\n(Leave blank to auto-generate a random 8-char password.)`,
      '',
    );
    if (proposed === null) return; // cancelled
    setResetting(user.id);
    try {
      const r = await axios.post(`${API_URL}/admin/users/${user.id}/reset-password`, {
        new_password: (proposed || '').trim(),
      });
      setResetDialog({ user, newPw: r.data.new_password });
      await fetchUsers();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Reset failed'));
    } finally {
      setResetting(null);
    }
  };

  const handleBulkReset = async () => {
    if ((bulkPw || '').trim().length < 4) {
      toast.error('Password must be at least 4 characters');
      return;
    }
    if (!window.confirm(
      `Apply password "${bulkPw}" to ${bulkIncludeAdmins ? 'ALL users including admins' : 'all non-admin users'}? ` +
      `Your own account (${currentUser?.email || currentUser?.username}) is NEVER touched.`,
    )) return;
    setBulkRunning(true);
    try {
      const r = await axios.post(`${API_URL}/admin/users/bulk-reset-passwords`, {
        new_password: bulkPw.trim(),
        include_admins: bulkIncludeAdmins,
      });
      setBulkResult(r.data);
      toast.success(`${r.data.count} passwords reset to "${r.data.new_password}"`);
      await fetchUsers();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Bulk reset failed'));
    } finally {
      setBulkRunning(false);
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt).then(
      () => toast.success('Copied'),
      () => toast.error('Copy failed'),
    );
  };

  const openEdit = (u) => setEditUser({
    id: u.id,
    full_name: u.full_name || '',
    username: u.username || '',
    email: u.email || '',
  });

  const saveEdit = async () => {
    if (!editUser) return;
    setEditSaving(true);
    try {
      await axios.patch(`${API_URL}/admin/users/${editUser.id}`, {
        full_name: editUser.full_name,
        username: editUser.username,
        email: editUser.email,
      });
      toast.success('User updated');
      setEditUser(null);
      await fetchUsers();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Update failed'));
    } finally {
      setEditSaving(false);
    }
  };

  const viewAs = async (u) => {
    if (u.role === 'admin') {
      toast.error('Cannot view as another admin');
      return;
    }
    if (!window.confirm(`Open ${u.full_name || u.email || u.username}'s dashboard in a new tab?\n\nYou'll see exactly what they see. Your own admin session stays logged in here.`)) return;
    try {
      const r = await axios.post(`${API_URL}/admin/users/${u.id}/impersonate`);
      const impUrl = `${window.location.origin}/impersonate#token=${encodeURIComponent(r.data.token)}`;
      window.open(impUrl, '_blank', 'noopener');
    } catch (e) {
      toast.error(getErrorMessage(e, 'Impersonation failed'));
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Users className="h-6 w-6" /> User Manager
            </h1>
            <p className="text-slate-300 text-sm">Reset passwords for any user. Your own account is never affected.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Input
              placeholder="Search by name, email, role…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-64 bg-slate-800 border-slate-700 text-white"
              data-testid="user-manager-search"
            />
            <Button
              onClick={() => setBulkOpen(true)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="user-manager-bulk-btn"
            >
              <KeyRound className="h-4 w-4 mr-1" /> Reset ALL Passwords
            </Button>
            <Button variant="outline" onClick={fetchUsers} disabled={loading} data-testid="user-manager-refresh">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {isMasterAdmin && <MasterKeyCard />}

        <Card className="bg-slate-900/70 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-lg">All Users ({filtered.length})</CardTitle>
            <CardDescription className="text-slate-400">
              Passwords are stored encrypted and can't be viewed. Reset a password to issue a new one (shown once).
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto rounded border border-slate-700">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-800/80">
                    <TableHead className="text-slate-300">User</TableHead>
                    <TableHead className="text-slate-300">Role</TableHead>
                    <TableHead className="text-slate-300">Password</TableHead>
                    <TableHead className="text-slate-300 text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">Loading…</TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="text-center text-slate-400 py-6">No users found</TableCell></TableRow>
                  ) : filtered.map((u) => (
                    <TableRow key={u.id} className="hover:bg-slate-800/60" data-testid={`user-row-${u.id}`}>
                      <TableCell>
                        <div className="text-white font-medium">{u.full_name || u.username || u.email}</div>
                        <div className="text-xs text-slate-400">{u.email || u.username}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={ROLE_COLOURS[u.role] || 'bg-slate-100 text-slate-700 border-slate-300'}>
                          {u.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        <span className="text-slate-500 italic">hidden</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 flex-wrap">
                          {isMasterAdmin && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEdit(u)}
                              data-testid={`edit-user-${u.id}`}
                              title="Edit name / username / email"
                            >
                              <Pencil className="h-3 w-3 mr-1" /> Edit
                            </Button>
                          )}
                          {isMasterAdmin && u.role !== 'admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-cyan-400 text-cyan-700 hover:bg-cyan-50"
                              onClick={() => viewAs(u)}
                              data-testid={`view-as-${u.id}`}
                              title="Open this user's dashboard in a new tab"
                            >
                              <LogIn className="h-3 w-3 mr-1" /> View As
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={resetting === u.id}
                            onClick={() => handleResetSingle(u)}
                            data-testid={`reset-pw-${u.id}`}
                          >
                            <KeyRound className="h-3 w-3 mr-1" />
                            {resetting === u.id ? 'Resetting…' : 'Reset'}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Edit user dialog (master admin only) */}
        <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
          <DialogContent data-testid="edit-user-dialog">
            <DialogHeader>
              <DialogTitle>Edit user</DialogTitle>
              <DialogDescription>Change display name, username or email. Use "Reset" to change the password.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Full name</Label>
                <Input value={editUser?.full_name || ''} onChange={(e) => setEditUser({ ...editUser, full_name: e.target.value })} data-testid="edit-user-full-name" />
              </div>
              <div>
                <Label>Username</Label>
                <Input value={editUser?.username || ''} onChange={(e) => setEditUser({ ...editUser, username: e.target.value })} data-testid="edit-user-username" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={editUser?.email || ''} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} data-testid="edit-user-email" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
              <Button onClick={saveEdit} disabled={editSaving} data-testid="edit-user-save">
                {editSaving ? 'Saving…' : 'Save changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Single-user reset result dialog */}
        <Dialog open={!!resetDialog} onOpenChange={(o) => !o && setResetDialog(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Password reset</DialogTitle>
              <DialogDescription>
                Give this to <b>{resetDialog?.user?.email || resetDialog?.user?.username}</b>. They can change it themselves after logging in.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-slate-100 border rounded p-3 font-mono text-lg flex items-center justify-between" data-testid="reset-result-pw">
              <span>{resetDialog?.newPw}</span>
              <Button size="sm" variant="outline" onClick={() => copy(resetDialog?.newPw || '')}>
                <Copy className="h-4 w-4 mr-1" /> Copy
              </Button>
            </div>
            <DialogFooter>
              <Button onClick={() => setResetDialog(null)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk reset dialog */}
        <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkResult(null); setBulkPw(''); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Reset ALL passwords</DialogTitle>
              <DialogDescription>
                Applies one password to every user (except your own logged-in account). Use a memorable password and give each user the new value.
              </DialogDescription>
            </DialogHeader>
            {!bulkResult ? (
              <div className="space-y-3">
                <div>
                  <Label>New password (applied to everyone)</Label>
                  <Input
                    value={bulkPw}
                    onChange={(e) => setBulkPw(e.target.value)}
                    placeholder="e.g. Welcome@2026"
                    data-testid="bulk-pw-input"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={bulkIncludeAdmins}
                    onChange={(e) => setBulkIncludeAdmins(e.target.checked)}
                    data-testid="bulk-include-admins"
                  />
                  Also reset other admin accounts (yours is always skipped)
                </label>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
                  <Button
                    onClick={handleBulkReset}
                    disabled={bulkRunning || bulkPw.length < 4}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="bulk-confirm-btn"
                  >
                    {bulkRunning ? 'Resetting…' : `Reset ${bulkIncludeAdmins ? 'ALL' : 'non-admin'} passwords`}
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm">
                  <b>{bulkResult.count}</b> user{bulkResult.count === 1 ? '' : 's'} now use the password:
                </p>
                <div className="bg-slate-100 border rounded p-3 font-mono text-lg flex items-center justify-between" data-testid="bulk-result-pw">
                  <span>{bulkResult.new_password}</span>
                  <Button size="sm" variant="outline" onClick={() => copy(bulkResult.new_password)}>
                    <Copy className="h-4 w-4 mr-1" /> Copy
                  </Button>
                </div>
                <div className="max-h-64 overflow-y-auto rounded border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email / username</TableHead>
                        <TableHead>Role</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bulkResult.users.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell className="font-mono text-xs">{u.email}</TableCell>
                          <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <DialogFooter>
                  <Button onClick={() => { setBulkOpen(false); setBulkResult(null); setBulkPw(''); }}>Done</Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
